<template>
  <div class="waitlist-page">
    <div class="navbar-host" :class="{ 'navbar-hidden': navbarHidden }">
      <Navbar />
    </div>

    <!-- Hero Section — tactile animated mesh -->
    <section
      ref="heroEl"
      class="hero-section"
      :class="{ 'hero-paused': !heroAnimating }"
      @mousemove="onHeroMouseMove"
    >
      <!-- Animated gradient mesh blobs (parallax + cursor-aware) -->
      <div
        class="mesh-blob mesh-blob-1"
        :style="{
          transform: `translate3d(${cursorX * 30}px, ${cursorY * 20}px, 0)`,
        }"
      ></div>
      <div
        class="mesh-blob mesh-blob-2"
        :style="{
          transform: `translate3d(${cursorX * -25}px, ${cursorY * 28}px, 0)`,
        }"
      ></div>
      <div
        class="mesh-blob mesh-blob-3"
        :style="{
          transform: `translate3d(${cursorX * 18}px, ${cursorY * -22}px, 0)`,
        }"
      ></div>
      <div
        class="mesh-blob mesh-blob-4"
        :style="{
          transform: `translate3d(${cursorX * -15}px, ${cursorY * 14}px, 0)`,
        }"
      ></div>

      <!-- Cursor-follow soft glow -->
      <div
        class="cursor-glow"
        :style="{
          left: cursorPxX + 'px',
          top: cursorPxY + 'px',
        }"
      ></div>

      <!-- Grain texture overlay -->
      <div class="grain"></div>

      <div class="hero-content">
        <h2 class="hero-headline font-bold cascade-2">
          Plan Less.<br />
          <span class="hero-headline-grad">Get Out More.</span>
        </h2>

        <!-- Tilly chat thread (ternapp BookScreen style) -->
        <div class="hero-chat cascade-3">
          <!-- PRE-SIGNUP: two intro bubbles (Tilly says hi, then asks for email)
               Avatar above first bubble on MOBILE, dangling left of last on DESKTOP. -->
          <template
            v-if="state !== 'subnew' && state !== 'subexists' && !loading"
          >
            <div class="hero-tilly-row has-avatar mobile-only-avatar">
              <div class="hero-tilly-avatar big-avatar">
                <TillyMascot variant="idle" cursor-track />
              </div>
              <div class="hero-tilly-bubble">
                <span class="hero-tilly-name">Tilly</span>
                hi! i'm Tilly ✨ i'm your personal social assistant. i learn
                what you love across restaurants, experiences, and travel, then
                recommend the best spots and make calls on your behalf, so your
                plans actually happen.💛
                <span class="hero-tilly-time">just now</span>
              </div>
            </div>
            <div class="hero-tilly-row has-avatar desktop-only-avatar">
              <div class="hero-tilly-avatar big-avatar">
                <TillyMascot variant="idle" cursor-track />
              </div>
              <div class="hero-tilly-bubble">
                i'm invite-only right now, so drop your email and i'll save your
                spot on the waitlist 👇
              </div>
            </div>
          </template>

          <!-- POST-SUBMIT (loading OR success): email stays mounted to avoid flicker -->
          <template v-else>
            <div class="hero-user-row">
              <div class="hero-user-bubble">{{ form.email }}</div>
            </div>

            <!-- Loading → response: dots fade out, bubbles stagger in -->
            <Transition name="typing-swap" mode="out-in">
              <div v-if="loading" key="dots" class="hero-tilly-row typing-row">
                <div class="hero-tilly-bubble hero-typing-bubble">
                  <span class="hero-typing-dot"></span>
                  <span class="hero-typing-dot"></span>
                  <span class="hero-typing-dot"></span>
                </div>
              </div>
              <div v-else key="responses" class="response-stack">
                <div class="hero-tilly-row has-avatar mobile-only-avatar">
                  <div class="hero-tilly-avatar big-avatar">
                    <TillyMascot variant="celebrate" />
                  </div>
                  <div class="hero-tilly-bubble">
                    <span class="hero-tilly-name">Tilly</span>
                    you're on the list 🎉 welcome to the inner circle. you're
                    <strong class="rewards-pos"
                      >#{{ currPriority || '—' }}</strong
                    >
                    in line right now
                  </div>
                </div>
                <div class="hero-tilly-row">
                  <div class="hero-tilly-bubble">
                    want to skip the line? refer 5 friends and i'll pull you to
                    the top. 🤫 every referral moves you up 5 spots!
                  </div>
                </div>
                <div class="hero-tilly-row has-avatar desktop-only-avatar">
                  <div class="hero-tilly-avatar big-avatar">
                    <TillyMascot variant="celebrate" />
                  </div>
                  <div class="hero-tilly-bubble bubble-link">
                    here's your link to share 👇
                    <span class="rewards-link" @click="copyHeroRef">{{
                      heroRefDisplay
                    }}</span>
                    <button class="rewards-copy-btn" @click="copyHeroRef">
                      {{ heroCopied ? '✓ copied, go share!' : 'copy & share' }}
                    </button>
                  </div>
                </div>
              </div>
            </Transition>
          </template>
        </div>

        <!-- Waitlist Form -->
        <div
          v-if="state !== 'subnew' && state !== 'subexists' && !loading"
          class="waitlist-form-container cascade-4"
        >
          <Error v-if="state === 'error'" @form-completed="state = $event" />
          <div v-else class="form-wrapper">
            <form @submit.prevent="onSubmit" class="messenger-form">
              <div class="messenger-bubble">
                <input
                  :value="displayedEmail"
                  @input="onEmailInput"
                  type="email"
                  required
                  placeholder="your email goes here…"
                  class="messenger-input"
                />
                <button
                  type="submit"
                  :disabled="loading || !displayedEmail"
                  class="messenger-send"
                  :aria-label="loading ? 'Joining…' : 'Join waitlist'"
                >
                  <svg
                    v-if="!loading"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a1 1 0 00-1.4 1.05l1.5 6.75c.07.32.32.57.64.66l8.86 1.94-8.86 1.94c-.32.09-.57.34-.64.66L2 19.35a1 1 0 001.4 1.05z"
                    />
                  </svg>
                  <span v-else class="ios-spinner" aria-hidden="true">
                    <span></span><span></span><span></span><span></span>
                    <span></span><span></span><span></span><span></span>
                  </span>
                </button>
              </div>
              <button
                type="submit"
                :disabled="loading"
                class="submit-button font-bold visually-hidden"
                tabindex="-1"
              >
                <span v-if="!loading">Join the waitlist</span>
                <span v-else>adding you...</span>
                <div v-if="!loading" class="star-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xml:space="preserve"
                    viewBox="0 0 784.11 815.53"
                  >
                    <path
                      class="fil0"
                      d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                    />
                  </svg>
                </div>
                <div v-if="!loading" class="star-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xml:space="preserve"
                    viewBox="0 0 784.11 815.53"
                  >
                    <path
                      class="fil0"
                      d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                    />
                  </svg>
                </div>
                <div v-if="!loading" class="star-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xml:space="preserve"
                    viewBox="0 0 784.11 815.53"
                  >
                    <path
                      class="fil0"
                      d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                    />
                  </svg>
                </div>
                <div v-if="!loading" class="star-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xml:space="preserve"
                    viewBox="0 0 784.11 815.53"
                  >
                    <path
                      class="fil0"
                      d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                    />
                  </svg>
                </div>
                <div v-if="!loading" class="star-5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xml:space="preserve"
                    viewBox="0 0 784.11 815.53"
                  >
                    <path
                      class="fil0"
                      d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                    />
                  </svg>
                </div>
                <div v-if="!loading" class="star-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    xml:space="preserve"
                    viewBox="0 0 784.11 815.53"
                  >
                    <path
                      class="fil0"
                      d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                    />
                  </svg>
                </div>
              </button>
            </form>
          </div>
        </div>

        <div class="social-links cascade-6">
          <a
            href="https://www.instagram.com/heytilly.ai"
            target="_blank"
            aria-label="Instagram"
            class="social-link"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <rect
                x="2"
                y="2"
                width="20"
                height="20"
                rx="5"
                ry="5"
                stroke="currentColor"
                fill="none"
                stroke-width="2"
              />
              <circle
                cx="12"
                cy="12"
                r="4"
                stroke="currentColor"
                fill="none"
                stroke-width="2"
              />
              <circle cx="18" cy="6" r="1.5" />
            </svg>
          </a>
          <a
            href="https://substack.com/@heytillyai"
            target="_blank"
            aria-label="Substack"
            class="social-link"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z"
              />
            </svg>
          </a>
          <a
            href="https://www.tiktok.com/@heytilly.ai"
            target="_blank"
            aria-label="TikTok"
            class="social-link"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"
              />
            </svg>
          </a>
        </div>
      </div>

      <!-- Living waves — pinned to the bottom of the hero section -->
      <div class="hero-waves" aria-hidden="true">
        <!-- Back wave: white, slower scroll -->
        <svg
          class="wave-layer wave-back"
          viewBox="0 0 2880 120"
          preserveAspectRatio="none"
        >
          <path
            fill="rgba(255,255,255,0.45)"
            d="M0,70 C360,110 720,30 1080,65 C1440,100 1800,20 2160,60 C2520,100 2700,40 2880,70 L2880,120 L0,120 Z"
          />
        </svg>
        <!-- Front wave: white — the CredibilityPanel handles the fade into cream below -->
        <svg
          class="wave-layer wave-front"
          viewBox="0 0 2880 120"
          preserveAspectRatio="none"
        >
          <path
            fill="#fafafa"
            d="M0,88 C360,60 720,110 1080,80 C1440,50 1800,105 2160,78 C2520,52 2700,95 2880,88 L2880,120 L0,120 Z"
          />
        </svg>
      </div>
    </section>

    <!-- Credibility Panel — 16k trips proof, the trust bar right under the hero -->
    <CredibilityPanel />

    <!-- Mascot manifesto / intro -->
    <MascotIntro />

    <!-- Cinematic feature stage — single sticky stage cross-fades between
         5 scenes (phone slides, text fades, accent tint shifts) -->
    <FeatureStage :scenes="featureScenes" />

    <!-- From the Founder — pull quote + story note -->
    <OurStorySection />

    <!-- More from Tilly — secondary entry-points. Visa is the free
         public tool; the planner is gated to existing Tern users and
         carries a small "For Tern users" badge so visitors aren't
         surprised by the login wall. Sits between the founder note
         and the final CTA. -->
    <section class="also-section" aria-label="More from Tilly">
      <div class="also-eyebrow">On the web, today</div>
      <h2 class="also-title">While the app brews.</h2>
      <p class="also-subtitle">
        These work in your browser right now. The full Tilly app is coming soon.
      </p>
      <div class="also-grid">
        <a href="/visa" class="also-tile tile-visa">
          <div class="tile-icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M2 12h20" />
              <path
                d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"
              />
            </svg>
          </div>
          <div class="tile-text">
            <div class="tile-title">Visa Checker</div>
            <div class="tile-meta">Free · any passport, any destination</div>
          </div>
          <svg
            class="tile-arrow"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="7" y1="17" x2="17" y2="7" />
            <polyline points="7 7 17 7 17 17" />
          </svg>
        </a>
        <router-link to="/travel" class="also-tile tile-planner">
          <div class="tile-icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"
              />
            </svg>
          </div>
          <div class="tile-text">
            <div class="tile-title">AI Trip Planner</div>
            <div class="tile-meta">For Tern users · log in to plan</div>
          </div>
          <svg
            class="tile-arrow"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="7" y1="17" x2="17" y2="7" />
            <polyline points="7 7 17 7 17 17" />
          </svg>
        </router-link>
      </div>
    </section>

    <!-- Final CTA Section — warm dark closer with "what's coming" teaser merged in -->
    <section
      ref="finalCtaEl"
      class="final-cta"
      :class="{ 'final-cta-paused': !finalCtaVisible }"
    >
      <!-- Top wave bridges the white founder section into mocha -->
      <svg
        class="cta-wave-top"
        viewBox="0 0 1440 60"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path fill="#ffffff" d="M0,30 C360,60 1080,0 1440,30 L1440,0 L0,0 Z" />
      </svg>

      <!-- Cinematic ambient orbs -->
      <div class="cta-bg-1"></div>
      <div class="cta-bg-2"></div>

      <!-- Floating sparkles -->
      <span class="cta-amb cta-amb-1">✦</span>
      <span class="cta-amb cta-amb-2">✧</span>
      <span class="cta-amb cta-amb-3">✦</span>
      <span class="cta-amb cta-amb-4">✧</span>
      <span class="cta-amb cta-amb-5">✦</span>

      <div class="cta-container">
        <!-- What's coming teaser -->
        <div class="cta-coming">
          <div class="cta-coming-label">What's coming</div>
          <ul class="cta-coming-list">
            <li>
              <svg
                class="cta-coming-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="9" cy="8" r="3" />
                <circle cx="17" cy="9" r="2.2" />
                <path d="M3 19a6 6 0 0 1 12 0M14 19a5 5 0 0 1 7-4" />
              </svg>
              <span
                ><strong>Plans for groups</strong><br />Plans around what
                everyone likes.</span
              >
            </li>
            <li>
              <svg
                class="cta-coming-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path
                  d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"
                />
              </svg>
              <span
                ><strong>Deeper nudges</strong><br />Plans you'll love, before
                you even ask.</span
              >
            </li>
            <li>
              <svg
                class="cta-coming-icon"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path
                  d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"
                />
              </svg>
              <span
                ><strong>Travel, integrated</strong><br />Tern, our AI travel
                planner, now inside Tilly.</span
              >
            </li>
          </ul>
        </div>

        <div class="cta-eyebrow">invite-only</div>
        <h2 class="cta-title">Ready to get out <em>more?</em></h2>
        <p class="cta-subtitle">Join the waitlist for early access to Tilly</p>

        <WaitlistRewards
          v-if="ctaState === 'subnew' || ctaState === 'subexists'"
          :refLink="ctaRefLink"
          :currPriority="ctaCurrPriority"
          :dark="true"
          panel="bottom"
          class="success-message"
        />
        <Error
          v-else-if="ctaState === 'error'"
          @form-completed="ctaState = $event"
        />
        <div v-else class="cta-form">
          <form @submit.prevent="onCtaSubmit" class="messenger-form">
            <div class="messenger-bubble">
              <input
                :value="ctaDisplayedEmail"
                @input="onCtaEmailInput"
                type="email"
                required
                placeholder="your email goes here..."
                class="messenger-input"
              />
              <button
                type="submit"
                :disabled="ctaLoading || !ctaDisplayedEmail"
                class="messenger-send"
                :aria-label="ctaLoading ? 'Joining…' : 'Join waitlist'"
              >
                <svg
                  v-if="!ctaLoading"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a1 1 0 00-1.4 1.05l1.5 6.75c.07.32.32.57.64.66l8.86 1.94-8.86 1.94c-.32.09-.57.34-.64.66L2 19.35a1 1 0 001.4 1.05z"
                  />
                </svg>
                <span v-if="ctaLoading" class="ios-spinner" aria-hidden="true">
                  <span></span><span></span><span></span><span></span>
                  <span></span><span></span><span></span><span></span>
                </span>
              </button>
            </div>
            <button
              type="submit"
              :disabled="ctaLoading"
              class="submit-button font-bold visually-hidden"
              tabindex="-1"
            >
              <span v-if="!ctaLoading">Join the waitlist</span>
              <span v-else>adding you...</span>
              <div v-if="!ctaLoading" class="star-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 784.11 815.53"
                >
                  <path
                    class="fil0"
                    d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                  />
                </svg>
              </div>
              <div v-if="!ctaLoading" class="star-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 784.11 815.53"
                >
                  <path
                    class="fil0"
                    d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                  />
                </svg>
              </div>
              <div v-if="!ctaLoading" class="star-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 784.11 815.53"
                >
                  <path
                    class="fil0"
                    d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                  />
                </svg>
              </div>
              <div v-if="!ctaLoading" class="star-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 784.11 815.53"
                >
                  <path
                    class="fil0"
                    d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                  />
                </svg>
              </div>
              <div v-if="!ctaLoading" class="star-5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 784.11 815.53"
                >
                  <path
                    class="fil0"
                    d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                  />
                </svg>
              </div>
              <div v-if="!ctaLoading" class="star-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xml:space="preserve"
                  viewBox="0 0 784.11 815.53"
                >
                  <path
                    class="fil0"
                    d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z"
                  />
                </svg>
              </div>
            </button>
          </form>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <WaitlistFooter />
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import * as amplitude from '@amplitude/analytics-browser';
import axios from 'axios';
import Navbar from '@/components/Navbar.vue';
import Error from '@/components/Form/Error.vue';
import OurStorySection from '@/components/OurStorySection.vue';
import WaitlistFooter from '@/components/WaitlistFooter.vue';
import CredibilityPanel from '@/components/CredibilityPanel.vue';
import TillyMascot from '@/components/TillyMascot.vue';
import MascotIntro from '@/components/MascotIntro.vue';
import FeatureStage from '@/components/FeatureStage.vue';
import WaitlistRewards from '@/components/WaitlistRewards.vue';

export default {
  name: 'Waitlist',
  components: {
    Navbar,
    Error,
    OurStorySection,
    WaitlistFooter,
    CredibilityPanel,
    TillyMascot,
    MascotIntro,
    FeatureStage,
    WaitlistRewards,
  },
  setup() {
    const form = ref({
      email: '',
      ref_link: '',
      current_priority: '',
    });

    const state = ref('');
    const refLink = ref('');
    const currPriority = ref('');
    const loading = ref(false);
    const isRecording = ref(false);
    const recognition = ref(null);

    // Separate state for CTA form
    const ctaForm = ref({
      email: '',
      ref_link: '',
      current_priority: '',
    });
    const ctaState = ref('');
    const ctaRefLink = ref('');
    const ctaCurrPriority = ref('');
    const ctaLoading = ref(false);
    const ctaIsRecording = ref(false);
    const ctaRecognition = ref(null);

    let recognitionSilenceTimeout = null;
    let ctaRecognitionSilenceTimeout = null;
    let previousEmailBeforeSession = '';
    let previousCtaEmailBeforeSession = '';
    let voiceSessionHadResult = false;
    let ctaVoiceSessionHadResult = false;
    const SILENCE_TIMEOUT_MS = 2000;

    const route = useRoute();

    // Hero rewards copy state (chat-thread continuation)
    const heroCopied = ref(false);
    const copyHeroRef = async () => {
      if (!refLink.value) return;
      try {
        await navigator.clipboard.writeText(refLink.value);
        heroCopied.value = true;
        if (typeof amplitude !== 'undefined') {
          try {
            amplitude.track('Referral Link Copied', { panel: 'hero_chat' });
          } catch (e) {
            /* ignore */
          }
        }
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'Tilly waitlist',
              text: "Join me on the waitlist for Tilly's - she's a personal concierge for experiences!",
              url: refLink.value,
            });
          } catch (e) {
            /* ignore cancel */
          }
        }
        setTimeout(() => {
          heroCopied.value = false;
        }, 2200);
      } catch (e) {
        /* ignore */
      }
    };

    const isMobile = ref(false);

    // Pause heavy mesh / wave / orb animations while the user has scrolled
    // past them — Safari keeps re-rasterizing offscreen composited layers
    // with blur filters, which accumulates GPU memory until it crashes.
    const heroEl = ref(null);
    const heroAnimating = ref(true);
    let heroPauseObserver = null;

    const finalCtaEl = ref(null);
    const finalCtaVisible = ref(true);
    let finalCtaPauseObserver = null;

    const updateIsMobile = () => {
      if (typeof window === 'undefined') return;
      isMobile.value = window.matchMedia('(max-width: 767px)').matches;
    };

    // Navbar visibility:
    //   • visible at the very top (page load, hero unscrolled)
    //   • hidden once the user starts scrolling inside the hero (so it stops
    //     overlapping the email pill / chat thread mid-scroll)
    //   • visible again after the user has scrolled past the hero
    const navbarHidden = ref(false);
    let navScrollHandler = null;

    // Feature stage scenes — order = scroll order. Each scene = one sticky
    // beat in the cinematic stage; the stage cross-fades between them.
    // Same scene order + captions as the investor decks:
    // Discover → Refine → Save → Visualize → Book.
    const featureScenes = [
      {
        mockup: 'happening',
        accent: 'teal',
        eyebrow: "DISCOVER WHAT'S HAPPENING",
        headline: 'This week, <em>around you.</em>',
        caption:
          "Tilly finds events, classes, markets and more curated for your taste. Change the location or dates to see what's happening during your trip.",
      },
      {
        mockup: 'rategame',
        accent: 'violet',
        eyebrow: 'GET INSPIRED',
        headline: 'Swipe. <em>Tilly learns.</em>',
        caption:
          'A few swipes add fresh inspo to your lists, teach Tilly what you love, and sharpen her recommendations.',
      },
      {
        mockup: 'snap',
        accent: 'blue',
        eyebrow: 'SAVE & AUTO-ORGANIZE LISTS',
        headline: 'See it. <em>Save it.</em>',
        caption:
          'Screenshot a cryptic post, paste a messy note or share a link. Tilly finds the spot and auto-organizes it for you.',
      },
      {
        mockup: 'taste',
        accent: 'amber',
        eyebrow: 'VISUALIZE YOUR TASTE',
        headline: 'Your taste, <em>decoded.</em>',
        caption:
          'Paste from ChatGPT or Claude, or play a quick this-or-that quiz. Tilly visualizes your taste in 60 seconds and makes it easy to edit.',
      },
      {
        mockup: 'booking',
        accent: 'coral',
        eyebrow: 'GET RECOMMENDATIONS & BOOK DINING',
        headline: 'Plans, <em>booked.</em>',
        caption:
          'Tilly handles the logistics — booking on Resy, Opentable, SevenRooms, or making live phone calls on your behalf.',
      },
    ];

    // Cursor parallax for hero mesh blobs
    const cursorX = ref(0); // -1 to 1 normalized
    const cursorY = ref(0);
    const cursorPxX = ref(0); // raw px for glow
    const cursorPxY = ref(0);
    const onHeroMouseMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      cursorX.value = (e.clientX - rect.left - cx) / cx;
      cursorY.value = (e.clientY - rect.top - cy) / cy;
      cursorPxX.value = e.clientX - rect.left;
      cursorPxY.value = e.clientY - rect.top;
    };

    const createTranscriptionAnimator = ({ initialValue = '', setValue }) => {
      const displayed = ref(initialValue);
      let queue = [];
      let timeoutId = null;

      const commit = (value) => {
        const normalizedValue =
          value === null || value === undefined ? '' : String(value);
        displayed.value = normalizedValue;
        setValue(normalizedValue);
      };

      const cancel = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        queue = [];
      };

      const randomDelay = (min, max) =>
        Math.floor(Math.random() * (max - min + 1)) + min;

      const sharedPrefixLength = (a = '', b = '') => {
        const maxLength = Math.min(a.length, b.length);
        let index = 0;
        while (index < maxLength && a[index] === b[index]) {
          index += 1;
        }
        return index;
      };

      const runNextStep = () => {
        if (!queue.length) {
          timeoutId = null;
          return;
        }

        const { value, delay } = queue.shift();
        commit(value);

        if (!queue.length) {
          timeoutId = null;
          return;
        }

        timeoutId = setTimeout(runNextStep, delay);
      };

      const animateTo = (target, { natural = true } = {}) => {
        const nextValue = target ?? '';

        if (!natural) {
          cancel();
          commit(nextValue);
          return;
        }

        const currentValue = displayed.value ?? '';

        if (currentValue === nextValue) {
          cancel();
          commit(nextValue);
          return;
        }

        cancel();

        const prefixLength = sharedPrefixLength(currentValue, nextValue);

        const removalSteps = [];
        const additionSteps = [];
        const removalTailLength = currentValue.length - prefixLength;
        const additionTailLength = nextValue.length - prefixLength;

        if (removalTailLength > 0) {
          if (removalTailLength <= 2) {
            for (let i = currentValue.length; i > prefixLength; i -= 1) {
              removalSteps.push({
                value: currentValue.slice(0, i - 1),
                delay: randomDelay(36, 70),
              });
            }
          } else {
            const targetValue = currentValue.slice(0, prefixLength);
            const midIndex =
              prefixLength + Math.max(1, Math.floor(removalTailLength * 0.4));
            removalSteps.push({
              value: currentValue.slice(0, midIndex),
              delay: randomDelay(45, 90),
            });
            removalSteps.push({
              value: targetValue,
              delay: randomDelay(45, 90),
            });
          }
        }

        if (additionTailLength > 0) {
          if (additionTailLength <= 2) {
            for (let i = prefixLength; i < nextValue.length; i += 1) {
              additionSteps.push({
                value: nextValue.slice(0, i + 1),
                delay: randomDelay(48, 92),
              });
            }
          } else {
            const midIndex =
              prefixLength + Math.max(1, Math.floor(additionTailLength * 0.6));
            additionSteps.push({
              value: nextValue.slice(0, midIndex),
              delay: randomDelay(55, 105),
            });
            additionSteps.push({
              value: nextValue,
              delay: randomDelay(55, 105),
            });
          }
        }

        queue = [...removalSteps, ...additionSteps];

        if (!queue.length) {
          commit(nextValue);
          return;
        }

        runNextStep();
      };

      const setImmediate = (value) => {
        animateTo(value, { natural: false });
      };

      return {
        displayed,
        animateTo,
        setImmediate,
        cancel,
      };
    };

    const emailAnimator = createTranscriptionAnimator({
      initialValue: form.value.email || '',
      setValue: (value) => {
        form.value.email = value;
      },
    });

    const ctaEmailAnimator = createTranscriptionAnimator({
      initialValue: ctaForm.value.email || '',
      setValue: (value) => {
        ctaForm.value.email = value;
      },
    });

    const displayedEmail = emailAnimator.displayed;
    const ctaDisplayedEmail = ctaEmailAnimator.displayed;

    const onEmailInput = (event) => {
      const value =
        (event?.target && typeof event.target.value === 'string'
          ? event.target.value
          : '') || '';
      emailAnimator.setImmediate(value);
      voiceSessionHadResult = value.length > 0;
    };

    const onCtaEmailInput = (event) => {
      const value =
        (event?.target && typeof event.target.value === 'string'
          ? event.target.value
          : '') || '';
      ctaEmailAnimator.setImmediate(value);
      ctaVoiceSessionHadResult = value.length > 0;
    };

    const normalizeSpokenEmail = (transcript) => {
      if (!transcript) {
        return '';
      }

      const words = transcript
        .toLowerCase()
        .replace(/[^a-z0-9@.\-_+\s]/g, ' ')
        .split(/\s+/)
        .filter(Boolean);

      const numberMap = {
        zero: '0',
        oh: '0',
        one: '1',
        two: '2',
        three: '3',
        four: '4',
        for: '4',
        five: '5',
        six: '6',
        seven: '7',
        eight: '8',
        nine: '9',
      };

      const replaceFirstVowel = (word, letter) => {
        const matchIndex = word.search(/[aeiouy]/);
        if (matchIndex !== -1) {
          return `${word.slice(0, matchIndex)}${letter}${word.slice(
            matchIndex + 1,
          )}`;
        }
        return `${word}${letter}`;
      };

      const letterNames = {
        a: 'a',
        ay: 'a',
        ei: 'a',
        b: 'b',
        bee: 'b',
        c: 'c',
        see: 'c',
        sea: 'c',
        d: 'd',
        dee: 'd',
        e: 'e',
        ee: 'e',
        i: 'i',
        eye: 'i',
        ai: 'i',
        o: 'o',
        oh: 'o',
        u: 'u',
        you: 'u',
        why: 'y',
      };

      const tokens = [];
      let i = 0;
      while (i < words.length) {
        const word = words[i];
        let handled = false;
        let incrementBy = 1;

        if (
          word === 'with' &&
          i + 2 < words.length &&
          (words[i + 1] === 'a' || words[i + 1] === 'an')
        ) {
          const letterKey = words[i + 2];
          const letter =
            letterNames[letterKey] ||
            (letterKey.length === 1 && /^[a-z]$/.test(letterKey)
              ? letterKey
              : '');

          if (letter && tokens.length > 0) {
            const prevTokenIndex = tokens.length - 1;
            const prevToken = tokens[prevTokenIndex];
            if (/^[a-z]+$/.test(prevToken)) {
              tokens[prevTokenIndex] = replaceFirstVowel(prevToken, letter);
              handled = true;
              incrementBy = 3;
            }
          }
        }

        if (!handled) {
          if (word === 'at' || (word === 'a' && words[i + 1] === 't')) {
            tokens.push('@');
            if (word === 'a' && words[i + 1] === 't') {
              incrementBy = Math.max(incrementBy, 2);
            }
            handled = true;
          } else if (word === 'dot' || word === 'period' || word === 'point') {
            tokens.push('.');
            handled = true;
          } else if (word === 'underscore') {
            tokens.push('_');
            handled = true;
          } else if (word === 'dash' || word === 'hyphen' || word === 'minus') {
            tokens.push('-');
            handled = true;
          } else if (word === 'plus') {
            tokens.push('+');
            if (words[i + 1] === 'sign') {
              incrementBy = Math.max(incrementBy, 2);
            }
            handled = true;
          } else if (word === 'space') {
            handled = true;
          } else if (numberMap[word]) {
            tokens.push(numberMap[word]);
            handled = true;
          } else if (/^[a-z]$/.test(word)) {
            tokens.push(word);
            handled = true;
          }
        }

        if (!handled && /^[a-z0-9@._+-]+$/.test(word)) {
          tokens.push(word);
        }

        i += incrementBy;
      }

      return tokens
        .join('')
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9@._+-]/g, '');
    };

    const ternApiEndpoint =
      process.env.VUE_APP_ENDPOINTS === 'production'
        ? 'tern-1d723'
        : 'tern-staging';

    onMounted(() => {
      const apiKey =
        process.env.VUE_APP_ENDPOINTS === 'production'
          ? 'a8a56f173be65492fcf99607fa54c5cc'
          : 'eec0ac28984d63289b4a88e42518d23f';

      // Mobile / desktop detection
      updateIsMobile();
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', updateIsMobile);
      }

      // Pause hero + final-CTA animations when offscreen (invisible to
      // the user, frees GPU memory so Safari doesn't crash).
      if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
        if (heroEl.value) {
          heroPauseObserver = new IntersectionObserver(
            ([entry]) => {
              heroAnimating.value = entry.isIntersecting;
            },
            { threshold: 0 },
          );
          heroPauseObserver.observe(heroEl.value);
        }
        if (finalCtaEl.value) {
          finalCtaPauseObserver = new IntersectionObserver(
            ([entry]) => {
              finalCtaVisible.value = entry.isIntersecting;
            },
            { threshold: 0 },
          );
          finalCtaPauseObserver.observe(finalCtaEl.value);
        }
      }

      // Navbar visibility — three zones:
      //   y < 24px            → at the very top, navbar visible
      //   24px ≤ y < heroEnd  → user is scrolling inside the hero, hide
      //   y ≥ heroEnd         → past the hero, navbar visible again
      navScrollHandler = () => {
        const y = window.scrollY;
        const heroEnd = window.innerHeight * 0.9; // close to one viewport
        navbarHidden.value = y >= 24 && y < heroEnd;
      };
      navScrollHandler();
      window.addEventListener('scroll', navScrollHandler, { passive: true });

      // Initialize Amplitude
      amplitude.init(apiKey, {
        defaultTracking: true,
        transport: 'beacon',
        flushIntervalMillis: 0,
        flushQueueSize: 1,
      });

      // Initialize Speech Recognition
      if (
        typeof window !== 'undefined' &&
        ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
      ) {
        const SpeechRecognition =
          window.SpeechRecognition || window.webkitSpeechRecognition;
        recognition.value = new SpeechRecognition();
        recognition.value.continuous = true;
        recognition.value.interimResults = true;
        recognition.value.lang = 'en-US';

        recognition.value.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map((result) => result[0])
            .map((result) => result.transcript)
            .join('');

          const normalized = normalizeSpokenEmail(transcript);
          emailAnimator.animateTo(normalized);
          if (normalized) {
            voiceSessionHadResult = true;
          }
          if (recognitionSilenceTimeout) {
            clearTimeout(recognitionSilenceTimeout);
          }
          recognitionSilenceTimeout = setTimeout(() => {
            if (isRecording.value && recognition.value) {
              recognition.value.stop();
            }
          }, SILENCE_TIMEOUT_MS);
        };

        recognition.value.onend = () => {
          isRecording.value = false;
          if (recognitionSilenceTimeout) {
            clearTimeout(recognitionSilenceTimeout);
            recognitionSilenceTimeout = null;
          }
          if (!voiceSessionHadResult && previousEmailBeforeSession) {
            emailAnimator.setImmediate(previousEmailBeforeSession);
          }
          previousEmailBeforeSession = '';
          voiceSessionHadResult = false;
        };

        recognition.value.onerror = () => {
          isRecording.value = false;
          if (recognitionSilenceTimeout) {
            clearTimeout(recognitionSilenceTimeout);
            recognitionSilenceTimeout = null;
          }
          if (!voiceSessionHadResult && previousEmailBeforeSession) {
            emailAnimator.setImmediate(previousEmailBeforeSession);
          }
          previousEmailBeforeSession = '';
          voiceSessionHadResult = false;
        };

        // Initialize CTA Speech Recognition (separate instance)
        ctaRecognition.value = new SpeechRecognition();
        ctaRecognition.value.continuous = true;
        ctaRecognition.value.interimResults = true;
        ctaRecognition.value.lang = 'en-US';

        ctaRecognition.value.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map((result) => result[0])
            .map((result) => result.transcript)
            .join('');

          const normalized = normalizeSpokenEmail(transcript);
          ctaEmailAnimator.animateTo(normalized);
          if (normalized) {
            ctaVoiceSessionHadResult = true;
          }
          if (ctaRecognitionSilenceTimeout) {
            clearTimeout(ctaRecognitionSilenceTimeout);
          }
          ctaRecognitionSilenceTimeout = setTimeout(() => {
            if (ctaIsRecording.value && ctaRecognition.value) {
              ctaRecognition.value.stop();
            }
          }, SILENCE_TIMEOUT_MS);
        };

        ctaRecognition.value.onend = () => {
          ctaIsRecording.value = false;
          if (ctaRecognitionSilenceTimeout) {
            clearTimeout(ctaRecognitionSilenceTimeout);
            ctaRecognitionSilenceTimeout = null;
          }
          if (!ctaVoiceSessionHadResult && previousCtaEmailBeforeSession) {
            ctaEmailAnimator.setImmediate(previousCtaEmailBeforeSession);
          }
          previousCtaEmailBeforeSession = '';
          ctaVoiceSessionHadResult = false;
        };

        ctaRecognition.value.onerror = () => {
          ctaIsRecording.value = false;
          if (ctaRecognitionSilenceTimeout) {
            clearTimeout(ctaRecognitionSilenceTimeout);
            ctaRecognitionSilenceTimeout = null;
          }
          if (!ctaVoiceSessionHadResult && previousCtaEmailBeforeSession) {
            ctaEmailAnimator.setImmediate(previousCtaEmailBeforeSession);
          }
          previousCtaEmailBeforeSession = '';
          ctaVoiceSessionHadResult = false;
        };
      }
    });

    const toggleVoiceInput = () => {
      if (!recognition.value) {
        console.warn('Voice input is not supported in your browser');
        return;
      }

      if (isRecording.value) {
        recognition.value.stop();
        isRecording.value = false;
        if (recognitionSilenceTimeout) {
          clearTimeout(recognitionSilenceTimeout);
          recognitionSilenceTimeout = null;
        }
      } else {
        amplitude.track('Voice Button Clicked (Started)', {
          source: 'main_waitlist',
        });
        previousEmailBeforeSession = displayedEmail.value || '';
        voiceSessionHadResult = false;
        emailAnimator.setImmediate('');
        recognition.value.start();
        isRecording.value = true;
      }
    };

    const toggleCtaVoiceInput = () => {
      if (!ctaRecognition.value) {
        console.warn('Voice input is not supported in your browser');
        return;
      }

      if (ctaIsRecording.value) {
        ctaRecognition.value.stop();
        ctaIsRecording.value = false;
        if (ctaRecognitionSilenceTimeout) {
          clearTimeout(ctaRecognitionSilenceTimeout);
          ctaRecognitionSilenceTimeout = null;
        }
      } else {
        amplitude.track('Voice Button Clicked (Started)', {
          source: 'cta_form',
        });
        previousCtaEmailBeforeSession = ctaDisplayedEmail.value || '';
        ctaVoiceSessionHadResult = false;
        ctaEmailAnimator.setImmediate('');
        ctaRecognition.value.start();
        ctaIsRecording.value = true;
      }
    };

    onBeforeUnmount(() => {
      if (navScrollHandler && typeof window !== 'undefined') {
        window.removeEventListener('scroll', navScrollHandler);
        navScrollHandler = null;
      }
      if (heroPauseObserver) {
        heroPauseObserver.disconnect();
        heroPauseObserver = null;
      }
      if (finalCtaPauseObserver) {
        finalCtaPauseObserver.disconnect();
        finalCtaPauseObserver = null;
      }
      if (recognition.value) {
        try {
          recognition.value.onresult = null;
          recognition.value.onerror = null;
          recognition.value.onend = null;
          recognition.value.stop();
        } catch (e) {
          // Ignore stop errors
        }
      }
      if (ctaRecognition.value) {
        try {
          ctaRecognition.value.onresult = null;
          ctaRecognition.value.onerror = null;
          ctaRecognition.value.onend = null;
          ctaRecognition.value.stop();
        } catch (e) {
          // Ignore stop errors
        }
      }
      if (recognitionSilenceTimeout) {
        clearTimeout(recognitionSilenceTimeout);
        recognitionSilenceTimeout = null;
      }
      if (ctaRecognitionSilenceTimeout) {
        clearTimeout(ctaRecognitionSilenceTimeout);
        ctaRecognitionSilenceTimeout = null;
      }

      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateIsMobile);
      }

      emailAnimator.cancel();
      ctaEmailAnimator.cancel();
    });

    const waitlistAPI = async () => {
      const apiKey =
        process.env.VUE_APP_ENDPOINTS === 'production' ? '31789' : '31807';
      const body = route.query.ref_id
        ? {
            email: form.value.email,
            api_key: apiKey,
            referral_link: window.location.href,
          }
        : { email: form.value.email, api_key: apiKey };

      const { data } = await axios.post(
        'https://api.getwaitlist.com/api/v1/waiter',
        body,
      );

      form.value.ref_link = data.referral_link;
      form.value.current_priority = data.priority;
      refLink.value = data.referral_link;
      currPriority.value = data.priority;
    };

    const mailchimpAPI = async () => {
      const formData = {
        email: form.value.email,
      };

      await axios.post(
        `https://us-central1-${ternApiEndpoint}.cloudfunctions.net/ternAPI/v1/mailchimpsub`,
        formData,
      );
    };

    const onSubmit = async () => {
      loading.value = true;
      try {
        await waitlistAPI();
        await mailchimpAPI();
        state.value = 'subnew';
        loading.value = false;

        // Track conversion
        amplitude.track('Waitlist Signed Up', {
          panel: 'top',
        });
      } catch (e) {
        if (e.response?.data?.title === 'Member Exists') {
          state.value = 'subexists';
          amplitude.track('Waitlist Already Signed Up', {
            panel: 'top',
          });
        } else {
          state.value = 'error';
        }
        loading.value = false;
      }
    };

    const ctaWaitlistAPI = async () => {
      const apiKey =
        process.env.VUE_APP_ENDPOINTS === 'production' ? '31789' : '31807';
      const body = route.query.ref_id
        ? {
            email: ctaForm.value.email,
            api_key: apiKey,
            referral_link: window.location.href,
          }
        : { email: ctaForm.value.email, api_key: apiKey };

      const { data } = await axios.post(
        'https://api.getwaitlist.com/api/v1/waiter',
        body,
      );

      ctaForm.value.ref_link = data.referral_link;
      ctaForm.value.current_priority = data.priority;
      ctaRefLink.value = data.referral_link;
      ctaCurrPriority.value = data.priority;
    };

    const ctaMailchimpAPI = async () => {
      const formData = {
        email: ctaForm.value.email,
      };

      await axios.post(
        `https://us-central1-${ternApiEndpoint}.cloudfunctions.net/ternAPI/v1/mailchimpsub`,
        formData,
      );
    };

    const onCtaSubmit = async () => {
      ctaLoading.value = true;
      try {
        await ctaWaitlistAPI();
        await ctaMailchimpAPI();
        ctaState.value = 'subnew';
        ctaLoading.value = false;

        // Track conversion
        amplitude.track('Waitlist Signed Up', {
          panel: 'bottom',
        });
      } catch (e) {
        if (e.response?.data?.title === 'Member Exists') {
          ctaState.value = 'subexists';
          amplitude.track('Waitlist Already Signed Up', {
            panel: 'bottom',
          });
        } else {
          ctaState.value = 'error';
        }
        ctaLoading.value = false;
      }
    };

    return {
      form,
      state,
      refLink,
      currPriority,
      loading,
      displayedEmail,
      isRecording,
      navbarHidden,
      featureScenes,
      ctaForm,
      ctaState,
      ctaRefLink,
      ctaCurrPriority,
      ctaLoading,
      ctaIsRecording,
      ctaDisplayedEmail,
      toggleVoiceInput,
      toggleCtaVoiceInput,
      onEmailInput,
      onCtaEmailInput,
      onSubmit,
      onCtaSubmit,
      isMobile,
      heroEl,
      heroAnimating,
      finalCtaEl,
      finalCtaVisible,
      heroCopied,
      copyHeroRef,
      cursorX,
      cursorY,
      cursorPxX,
      cursorPxY,
      onHeroMouseMove,
      heroRefDisplay: computed(() => {
        if (!refLink.value)
          return `${
            typeof window !== 'undefined' ? window.location.host : 'heytilly.ai'
          }/?ref_id=…`;
        return refLink.value.replace(/^https?:\/\//, '');
      }),
    };
  },
};
</script>

<style scoped>
/* Navbar host — slides the navbar out of view while the hero is showing
   (the hero email pill sits where the navbar would otherwise overlap), and
   slides it back in once the user scrolls past the hero. */
.navbar-host {
  position: sticky;
  top: 0;
  z-index: 50;
  transition: transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1),
    opacity 0.3s ease;
  will-change: transform, opacity;
}
.navbar-host.navbar-hidden {
  transform: translateY(-110%);
  opacity: 0;
  pointer-events: none;
}
@media (prefers-reduced-motion: reduce) {
  .navbar-host {
    transition: opacity 0.2s ease;
  }
  .navbar-host.navbar-hidden {
    transform: none;
  }
}

.waitlist-page {
  min-height: 100vh;
  background: #ffffff;
}

/* ===========================================================
   HERO — tactile, living gradient mesh (no video)
   =========================================================== */
.hero-section {
  position: relative;
  overflow: hidden;
  /* extra bottom padding so wave doesn't overlap social/trust strip */
  padding: 5rem 1.5rem clamp(90px, 12vw, 160px);
  /* Warm cream base — paper-like, loreobsessed energy */
  background: radial-gradient(
    ellipse 80% 60% at 50% 0%,
    #fff7e8 0%,
    #fef0d8 40%,
    #faddc0 100%
  );
  background-attachment: local;
  /* Confine paint — nothing inside the hero can affect rendering
     outside it, so the browser can skip offscreen children. */
  contain: paint;
}
/* Pause heavy animations when the hero is scrolled offscreen. User
   never sees it, compositor frees the blur buffers. */
.hero-paused .mesh-blob,
.hero-paused .wave-back,
.hero-paused .wave-front {
  animation-play-state: paused;
}
@media (min-width: 768px) {
  .hero-section {
    min-height: 100vh;
    /* Extra top breathing room on desktop so the headline lives
       below the navbar's visual weight rather than kissing it. */
    padding-top: 8rem;
  }
}
.hero-content {
  margin-left: auto;
  margin-right: auto;
}

/* Floating organic gradient blobs — the "live" feel.
   On desktop we use filter: blur() for the characteristic soft glow.
   On mobile iOS we swap the blur filter for a gradual-falloff radial
   gradient (added via per-blob override below) so the compositor
   doesn't have to allocate a fresh GPU blur buffer per frame. */
.mesh-blob {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  transition: transform 0.6s cubic-bezier(0.22, 0.61, 0.36, 1);
  will-change: transform;
}
@media (min-width: 768px) {
  .mesh-blob {
    filter: blur(60px);
  }
}
.mesh-blob-1 {
  width: 95vw;
  height: 95vw;
  max-width: 720px;
  max-height: 720px;
  top: -25%;
  left: -25%;
  background: radial-gradient(
    circle,
    rgba(253, 173, 0, 0.7) 0%,
    transparent 65%
  );
  animation: blob-drift-1 22s ease-in-out infinite;
}
.mesh-blob-2 {
  width: 90vw;
  height: 90vw;
  max-width: 640px;
  max-height: 640px;
  top: 10%;
  right: -30%;
  background: radial-gradient(
    circle,
    rgba(244, 132, 108, 0.7) 0%,
    transparent 65%
  );
  animation: blob-drift-2 28s ease-in-out infinite;
}
.mesh-blob-3 {
  width: 80vw;
  height: 80vw;
  max-width: 600px;
  max-height: 600px;
  bottom: -25%;
  left: 5%;
  background: radial-gradient(
    circle,
    rgba(123, 184, 232, 0.6) 0%,
    transparent 65%
  );
  animation: blob-drift-3 26s ease-in-out infinite;
}
.mesh-blob-4 {
  width: 70vw;
  height: 70vw;
  max-width: 480px;
  max-height: 480px;
  top: 55%;
  right: 10%;
  background: radial-gradient(
    circle,
    rgba(213, 173, 245, 0.55) 0%,
    transparent 65%
  );
  animation: blob-drift-4 32s ease-in-out infinite;
}
@media (min-width: 768px) {
  .mesh-blob {
    filter: blur(80px);
  }
  .mesh-blob-1 {
    width: 55vw;
    height: 55vw;
    top: -10%;
    left: -8%;
    background: radial-gradient(
      circle,
      rgba(253, 173, 0, 0.55) 0%,
      transparent 70%
    );
  }
  .mesh-blob-2 {
    width: 50vw;
    height: 50vw;
    top: 20%;
    right: -10%;
    background: radial-gradient(
      circle,
      rgba(244, 132, 108, 0.5) 0%,
      transparent 70%
    );
  }
  .mesh-blob-3 {
    width: 48vw;
    height: 48vw;
    bottom: -15%;
    left: 15%;
    background: radial-gradient(
      circle,
      rgba(123, 184, 232, 0.45) 0%,
      transparent 70%
    );
  }
  .mesh-blob-4 {
    width: 38vw;
    height: 38vw;
    top: 50%;
    right: 25%;
    background: radial-gradient(
      circle,
      rgba(213, 173, 245, 0.4) 0%,
      transparent 70%
    );
  }
}

@keyframes blob-drift-1 {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  33% {
    transform: translate3d(40px, 60px, 0) scale(1.08);
  }
  66% {
    transform: translate3d(-30px, 40px, 0) scale(0.95);
  }
}
@keyframes blob-drift-2 {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(-50px, -40px, 0) scale(1.1);
  }
}
@keyframes blob-drift-3 {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(60px, -50px, 0) scale(0.92);
  }
}
@keyframes blob-drift-4 {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  40% {
    transform: translate3d(-40px, 30px, 0) scale(1.15);
  }
  80% {
    transform: translate3d(30px, -20px, 0) scale(0.9);
  }
}

/* Mobile: serve the blob as a TRUE gaussian-blurred raster via an
   SVG mask (blur-mask.svg contains feGaussianBlur baked in) tinted
   per-blob with background-color. Browser rasterizes the SVG once
   and caches; subsequent frames composite the cached bitmap instead
   of recomputing blur pixels. Pixel-identical to `filter: blur()`,
   running at zero per-frame GPU cost. */
@media (max-width: 767px) {
  .mesh-blob {
    border-radius: 0;
    mask-image: url('~@/assets/ambient/blur-mask.svg');
    -webkit-mask-image: url('~@/assets/ambient/blur-mask.svg');
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-position: center;
  }
  .mesh-blob-1 {
    background: rgba(253, 173, 0, 0.7);
  }
  .mesh-blob-2 {
    background: rgba(244, 132, 108, 0.7);
  }
  .mesh-blob-3 {
    background: rgba(123, 184, 232, 0.6);
  }
  .mesh-blob-4 {
    background: rgba(213, 173, 245, 0.55);
  }
}

/* Cursor glow follows pointer for live tactile feel */
.cursor-glow {
  position: absolute;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 240, 220, 0.3) 35%,
    transparent 65%
  );
  filter: blur(28px);
  pointer-events: none;
  z-index: 1;
  transform: translate(-50%, -50%);
  mix-blend-mode: screen;
  transition: opacity 0.3s ease;
  display: none;
}
@media (hover: hover) and (pointer: fine) {
  .cursor-glow {
    display: block;
  }
}

/* Living waves — pinned to the bottom of the hero section (absolute inside position:relative section) */
.hero-waves {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: clamp(60px, 8vw, 120px);
  pointer-events: none;
  overflow: hidden;
  /* Transparent — hero gradient shows through; SVGs paint the white transition */
  background: transparent;
  z-index: 3;
}
.wave-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 200%; /* SVG viewBox has 2 wavelengths; render at 200vw */
  height: 100%;
  display: block;
  will-change: transform;
}
/* Translate by exactly one wavelength (50% of the SVG width) for seamless loop */
.wave-back {
  animation: wave-flow 18s linear infinite;
}
.wave-front {
  animation: wave-flow 11s linear infinite reverse;
}

@keyframes wave-flow {
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(-50%, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .wave-layer {
    animation: none;
  }
}

/* Grain texture — paper feel */
.grain {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  opacity: 0.32;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix type='matrix' values='1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.5 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
  background-size: 220px 220px;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 600px;
  width: 100%;
  text-align: center;
  color: #1a1a1a;
  overflow: visible;
  padding-bottom: 2rem;
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
@media (max-width: 430px) {
  .hero-content {
    padding-bottom: 0.75rem;
    padding-top: 1.25rem;
  }
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.85);
  color: #1a1a1a;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.hero-badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #43a047;
  box-shadow: 0 0 8px rgba(67, 160, 71, 0.7);
  animation: hero-dot-pulse 2s ease-in-out infinite;
}

@keyframes hero-dot-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.55;
    transform: scale(0.85);
  }
}

/* ===========================================================
   Hero headline (premium, big, gradient slice)
   =========================================================== */
.hero-headline {
  font-family: 'AirbnbCereal_W_Bd', sans-serif;
  font-size: clamp(2.6rem, 7vw, 4.5rem);
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.04em;
  margin: 1rem 0 1.75rem;
  color: #1a1410;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
}
.hero-headline-grad {
  display: inline-block;
  background: linear-gradient(
    100deg,
    #d97706 0%,
    #ea580c 28%,
    #be185d 55%,
    #7c3aed 80%,
    #2563eb 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  background-size: 200% 200%;
  animation: shine 6s ease-in-out infinite;
}
@keyframes shine {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* ===========================================================
   Hero chat preview (BookScreen-inspired)
   =========================================================== */
.hero-chat {
  width: 100%;
  max-width: 460px;
  margin: 0.5rem auto 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  text-align: left;
}
/* Desktop: add breathing room between headline and first bubble */
@media (min-width: 768px) {
  .hero-chat {
    margin-top: 2rem;
  }
}
/* On small mobile: pull form flush to bubbles */
@media (max-width: 430px) {
  .hero-chat {
    margin-bottom: 0.25rem;
  }
}

.hero-tilly-row {
  display: flex;
  align-items: flex-end;
  gap: 0.65rem;
}

/* Avatar dangles to the LEFT of the bubble (outside the chat column).
   Bubbles stay flush-left; only the last message's avatar pokes out. */
.hero-tilly-avatar {
  flex-shrink: 0;
  width: 0;
  height: 0;
  position: relative;
}
.invisible-spacer {
  display: none;
}

.hero-tilly-row {
  position: relative;
}

/* Base big-avatar styles (size + halo + drop-shadow) */
.hero-tilly-avatar.big-avatar {
  pointer-events: none;
  z-index: 4;
}
.hero-tilly-avatar.big-avatar :deep(.mascot-wrap) {
  width: 100%;
  max-width: 100%;
  filter: drop-shadow(0 8px 16px rgba(88, 168, 225, 0.4));
}
.hero-tilly-avatar.big-avatar :deep(.mascot-spark) {
  display: none;
}
.hero-tilly-avatar.big-avatar :deep(.mascot-halo) {
  inset: -8%;
  filter: blur(20px);
  opacity: 0.85;
}

/* ===== DESKTOP (≥1024px) =====
   Mascot dangles outside the chat column on the LAST Tilly bubble (left side). */
@media (min-width: 1024px) {
  /* Hide the mobile-only avatar */
  .mobile-only-avatar .hero-tilly-avatar.big-avatar {
    display: none;
  }

  /* Show the desktop-only avatar dangling left of its bubble */
  .desktop-only-avatar .hero-tilly-avatar.big-avatar {
    position: absolute;
    width: 84px;
    height: 84px;
    left: -100px;
    bottom: 0;
  }
}
@media (min-width: 1280px) {
  .desktop-only-avatar .hero-tilly-avatar.big-avatar {
    width: 96px;
    height: 96px;
    left: -116px;
  }
}

/* ===== MOBILE + TABLET (≤1023px) =====
   Mascot perches ABOVE the FIRST Tilly bubble like a profile picture. */
@media (max-width: 1023px) {
  /* Hide the desktop-only avatar */
  .desktop-only-avatar .hero-tilly-avatar.big-avatar {
    display: none;
  }

  /* Stack avatar above the bubble */
  .mobile-only-avatar.hero-tilly-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    padding-left: 0;
  }
  .mobile-only-avatar .hero-tilly-avatar.big-avatar {
    position: relative;
    width: 126px;
    height: 126px;
    left: 0;
    bottom: auto;
    top: auto;
    margin-left: -10px;
    margin-bottom: -22px;
  }
  .mobile-only-avatar .hero-tilly-bubble {
    width: 100%;
  }
}
@media (max-width: 480px) {
  .mobile-only-avatar .hero-tilly-avatar.big-avatar {
    width: 108px;
    height: 108px;
    margin-bottom: -18px;
  }
}

.hero-tilly-bubble {
  position: relative;
  flex: 1;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: #111;
  padding: 0.75rem 1rem 0.85rem;
  border-radius: 20px 20px 20px 4px;
  font-family: 'AirbnbCereal_W_Bk', sans-serif;
  font-size: 0.95rem;
  line-height: 1.45;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18),
    inset 0 1px 1px rgba(255, 255, 255, 0.6);
  text-align: left;
}
.hero-tilly-name {
  display: block;
  font-family: 'AirbnbCereal_W_Bd', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  color: #58a8e1;
  margin-bottom: 0.15rem;
}
.hero-tilly-time {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.7rem;
  color: #8e8e93;
  font-style: italic;
}

/* Typing indicator */
.hero-typing-bubble {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 14px 16px;
  min-height: 46px;
  flex: 0 0 auto;
  width: auto;
}
.hero-typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #94a3b8;
  flex-shrink: 0;
  animation: hero-typing-bounce 0.6s ease-in-out infinite;
}
.hero-typing-dot:nth-child(1) {
  animation-delay: 0ms;
}
.hero-typing-dot:nth-child(2) {
  animation-delay: 150ms;
}
.hero-typing-dot:nth-child(3) {
  animation-delay: 300ms;
}
@keyframes hero-typing-bounce {
  0%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

/* Typing dots leave, then responses enter (mode="out-in") */
.typing-swap-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.typing-swap-leave-from {
  opacity: 1;
}
.typing-swap-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.94);
}
.typing-swap-enter-active {
  transition: opacity 0.01s;
}
.typing-swap-enter-from,
.typing-swap-enter-to {
  opacity: 1;
}

/* Response stack: flex column matching hero-chat gap */
.response-stack {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

/* User reply bubble (sent — blue iMessage) */
.hero-user-row {
  display: flex;
  justify-content: flex-end;
  animation: bubble-slide-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.hero-user-bubble {
  max-width: 75%;
  background: linear-gradient(135deg, #58a8e1 0%, #4a90c9 100%);
  color: white;
  padding: 0.6rem 0.95rem;
  border-radius: 20px 20px 4px 20px;
  font-size: 0.95rem;
  font-family: 'AirbnbCereal_W_Bk', sans-serif;
  word-break: break-all;
  box-shadow: 0 8px 18px rgba(88, 168, 225, 0.35);
}

.invisible-spacer {
  width: 48px;
  visibility: hidden;
  flex-shrink: 0;
}

.rewards-pos {
  font-family: 'AirbnbCereal_W_Bd', sans-serif;
  font-weight: 800;
  background: linear-gradient(135deg, #fdad00 0%, #f4846c 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-size: 1.15em;
  letter-spacing: -0.01em;
}

/* Link bubble: Tilly's "here's your link" message */
.bubble-link {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.rewards-link {
  display: inline-block;
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  font-weight: 600;
  color: #58a8e1;
  background: rgba(88, 168, 225, 0.1);
  padding: 0.45rem 0.7rem;
  border-radius: 10px;
  font-size: 0.82rem;
  word-break: break-all;
  cursor: pointer;
  transition: background 0.2s ease;
  align-self: flex-start;
}
.rewards-link:hover {
  background: rgba(88, 168, 225, 0.18);
}
.rewards-copy-btn {
  align-self: flex-start;
  background: linear-gradient(135deg, #58a8e1 0%, #4a90c9 100%);
  color: white;
  border: none;
  border-radius: 999px;
  padding: 0.55rem 1.1rem;
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s ease;
  box-shadow: 0 4px 12px rgba(88, 168, 225, 0.35);
}
.rewards-copy-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(88, 168, 225, 0.5);
}

@keyframes bubble-slide-in {
  0% {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Stagger Tilly bubble entrance for chat-like cadence */
.hero-chat .hero-tilly-row,
.hero-chat .hero-user-row {
  opacity: 0;
  animation: bubble-slide-in 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.hero-chat .hero-tilly-row:nth-of-type(1) {
  animation-delay: 0.05s;
}
.hero-chat .hero-tilly-row:nth-of-type(2),
.hero-chat .hero-user-row:nth-of-type(2) {
  animation-delay: 0.85s;
}
.hero-chat .hero-tilly-row:nth-of-type(3) {
  animation-delay: 1.7s;
}
.hero-chat .hero-tilly-row:nth-of-type(4) {
  animation-delay: 2.5s;
}

/* Typing dots row: bypass the cascade animation/delay (we want it visible immediately) */
.hero-chat .hero-tilly-row.typing-row {
  animation: none;
  opacity: 1;
}

/* Response-stack bubbles: override nth-of-type delays with fast iMessage stagger.
   Uses existing bubble-slide-in keyframe. Comes after nth-of-type rules so wins. */
.response-stack .hero-tilly-row {
  animation: bubble-slide-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
.response-stack .hero-tilly-row:nth-child(1) {
  animation-delay: 0s;
}
.response-stack .hero-tilly-row:nth-child(2) {
  animation-delay: 0.22s;
}
.response-stack .hero-tilly-row:nth-child(3) {
  animation-delay: 0.44s;
}

/* ============================================
   Ternapp onboarding-style cascade entrance
   ============================================ */
.cascade-1,
.cascade-2,
.cascade-3,
.cascade-4,
.cascade-6 {
  opacity: 0;
  transform: translateY(16px);
  animation: hero-cascade 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.cascade-1 {
  animation-delay: 0.05s;
}
.cascade-2 {
  animation-delay: 0.2s;
}
.cascade-3 {
  animation-delay: 0.55s;
}
.cascade-4 {
  animation-delay: 0.8s;
}
.cascade-6 {
  animation-delay: 1.3s;
}

@keyframes hero-cascade {
  0% {
    opacity: 0;
    transform: translateY(16px);
    filter: blur(4px);
  }
  60% {
    opacity: 1;
    transform: translateY(-2px);
    filter: blur(0);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cascade-1,
  .cascade-2,
  .cascade-3,
  .cascade-4,
  .cascade-6 {
    opacity: 1;
    transform: none;
    animation: none;
  }
}

/* Waitlist Form */
.waitlist-form-container {
  width: 100%;
  margin-bottom: 2rem;
}
@media (max-width: 1023px) {
  .waitlist-form-container {
    margin-bottom: 1rem;
  }
}

/* Visually hidden but still focusable for AT */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
  pointer-events: none;
  opacity: 0 !important;
}

/* ===========================================================
   Messenger-style composer (iMessage-inspired, ternapp feel)
   =========================================================== */
.messenger-form {
  width: 100%;
  max-width: 560px;
  margin: 1.25rem auto 0;
  display: flex;
  flex-direction: column;
}
@media (max-width: 1023px) {
  .messenger-form {
    margin-top: 0.4rem;
  }
}

.messenger-bubble {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(24px) saturate(150%);
  -webkit-backdrop-filter: blur(24px) saturate(150%);
  border: 1.5px solid rgba(255, 255, 255, 0.75);
  border-radius: 999px;
  padding: 0.5rem 0.5rem 0.5rem 1.5rem;
  box-shadow: 0 16px 36px rgba(180, 80, 30, 0.12),
    0 4px 10px rgba(0, 0, 0, 0.05), inset 0 1px 1px rgba(255, 255, 255, 0.85);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease,
    transform 0.3s ease;
}
.messenger-bubble:focus-within {
  background: rgba(255, 255, 255, 0.75);
  border-color: rgba(88, 168, 225, 0.55);
  transform: translateY(-2px);
  box-shadow: 0 0 0 4px rgba(88, 168, 225, 0.16),
    0 20px 44px rgba(180, 80, 30, 0.16),
    inset 0 1px 1px rgba(255, 255, 255, 0.95);
}

.messenger-input {
  flex: 1;
  border: none;
  background: transparent;
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: 1.05rem;
  color: #1a1a1a;
  padding: 0.95rem 0.5rem 0.95rem 0;
  outline: none;
  min-width: 0;
}
.messenger-input::placeholder {
  color: rgba(60, 50, 40, 0.45);
  font-style: normal;
}

.messenger-send {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #58a8e1 0%, #4a90c9 100%);
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.25s ease, background 0.25s ease, opacity 0.2s ease;
  box-shadow: 0 6px 16px rgba(88, 168, 225, 0.4);
  opacity: 1;
}
.messenger-send svg {
  width: 22px;
  height: 22px;
  transform: translateX(-1px);
}

/* iOS-style activity indicator — 8 spokes, ternYellow, fits inside send button */
.ios-spinner {
  position: relative;
  display: block;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
.ios-spinner span {
  position: absolute;
  /* bar starts 2px from top; container is 20px → center at 10px
     transform-origin y = center(10) - bar-top(2) = 8px  ✓ */
  top: 2px;
  left: 50%;
  width: 2.5px;
  height: 6px;
  margin-left: -1.25px;
  background: #ffffff;
  border-radius: 2px;
  transform-origin: 1.25px 8px;
  animation: ios-spin 0.8s linear infinite;
  opacity: 0.25;
}
/* 8 spokes × 45° each; stagger = 0.8s / 8 = 0.1s */
.ios-spinner span:nth-child(1) {
  transform: rotate(0deg);
  animation-delay: -0.7s;
}
.ios-spinner span:nth-child(2) {
  transform: rotate(45deg);
  animation-delay: -0.6s;
}
.ios-spinner span:nth-child(3) {
  transform: rotate(90deg);
  animation-delay: -0.5s;
}
.ios-spinner span:nth-child(4) {
  transform: rotate(135deg);
  animation-delay: -0.4s;
}
.ios-spinner span:nth-child(5) {
  transform: rotate(180deg);
  animation-delay: -0.3s;
}
.ios-spinner span:nth-child(6) {
  transform: rotate(225deg);
  animation-delay: -0.2s;
}
.ios-spinner span:nth-child(7) {
  transform: rotate(270deg);
  animation-delay: -0.1s;
}
.ios-spinner span:nth-child(8) {
  transform: rotate(315deg);
  animation-delay: 0s;
}

@keyframes ios-spin {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0.25;
  }
}
.messenger-send:hover:not(:disabled) {
  transform: scale(1.08);
  box-shadow: 0 10px 22px rgba(88, 168, 225, 0.55);
}
.messenger-send:active:not(:disabled) {
  transform: scale(0.94);
}
.messenger-send:disabled {
  background: rgba(88, 168, 225, 0.55);
  color: #fff;
  box-shadow: none;
  cursor: default;
  opacity: 1;
}
.messenger-send-legacy-noop:hover:not(:disabled) {
  transform: scale(1.08);
  box-shadow: 0 10px 22px rgba(88, 168, 225, 0.6);
}

.cta-form .messenger-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.form-wrapper {
  width: 100%;
  margin: 0 auto;
  overflow: visible;
}

.submit-button {
  position: relative;
  padding: 1rem 2.5rem;
  background: #58a8e1;
  color: white;
  border: 2px solid #58a8e1;
  border-radius: 200px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  box-shadow: 0 0 0 #fdad00;
  overflow: visible;
}

.submit-button .fil0 {
  fill: white;
}

.submit-button .star-1 {
  position: absolute;
  top: 20%;
  left: 20%;
  width: 25px;
  height: auto;
  filter: drop-shadow(0 0 0 #fff);
  z-index: -5;
  transition: all 1s cubic-bezier(0.05, 0.83, 0.43, 0.96);
}

.submit-button .star-2 {
  position: absolute;
  top: 45%;
  left: 45%;
  width: 15px;
  height: auto;
  filter: drop-shadow(0 0 0 #fff);
  z-index: -5;
  transition: all 1s cubic-bezier(0, 0.4, 0, 1.01);
}

.submit-button .star-3 {
  position: absolute;
  top: 40%;
  left: 40%;
  width: 5px;
  height: auto;
  filter: drop-shadow(0 0 0 #fff);
  z-index: -5;
  transition: all 1s cubic-bezier(0, 0.4, 0, 1.01);
}

.submit-button .star-4 {
  position: absolute;
  top: 20%;
  left: 40%;
  width: 8px;
  height: auto;
  filter: drop-shadow(0 0 0 #fff);
  z-index: -5;
  transition: all 0.8s cubic-bezier(0, 0.4, 0, 1.01);
}

.submit-button .star-5 {
  position: absolute;
  top: 25%;
  left: 45%;
  width: 15px;
  height: auto;
  filter: drop-shadow(0 0 0 #fff);
  z-index: -5;
  transition: all 0.6s cubic-bezier(0, 0.4, 0, 1.01);
}

.submit-button .star-6 {
  position: absolute;
  top: 5%;
  left: 50%;
  width: 5px;
  height: auto;
  filter: drop-shadow(0 0 0 #fff);
  z-index: -5;
  transition: all 0.8s ease;
}

.submit-button:hover:not(:disabled) {
  background: transparent;
  color: white;
  border: 2px solid #a5c8e1;
  box-shadow: 0 0 10px #a5c8e1;
}

.submit-button:hover:not(:disabled) .fil0 {
  fill: white;
}

.submit-button:hover:not(:disabled) .star-1 {
  position: absolute;
  top: -80%;
  left: -30%;
  width: 25px;
  height: auto;
  filter: drop-shadow(0 0 0px #fff);
  z-index: 2;
}

.submit-button:hover:not(:disabled) .star-2 {
  position: absolute;
  top: -25%;
  left: 10%;
  width: 15px;
  height: auto;
  filter: drop-shadow(0 0 0px #fff);
  z-index: 2;
}

.submit-button:hover:not(:disabled) .star-3 {
  position: absolute;
  top: 55%;
  left: 25%;
  width: 5px;
  height: auto;
  filter: drop-shadow(0 0 0px #fff);
  z-index: 2;
}

.submit-button:hover:not(:disabled) .star-4 {
  position: absolute;
  top: 30%;
  left: 80%;
  width: 8px;
  height: auto;
  filter: drop-shadow(0 0 0px #fff);
  z-index: 2;
}

.submit-button:hover:not(:disabled) .star-5 {
  position: absolute;
  top: 25%;
  left: 115%;
  width: 15px;
  height: auto;
  filter: drop-shadow(0 0 0px #fff);
  z-index: 2;
}

.submit-button:hover:not(:disabled) .star-6 {
  position: absolute;
  top: 5%;
  left: 60%;
  width: 5px;
  height: auto;
  filter: drop-shadow(0 0 0px #fff);
  z-index: 2;
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.social-links {
  display: flex;
  gap: 1.85rem;
  justify-content: center;
  margin-top: 2.5rem;
  margin-bottom: 0.85rem;
}
@media (max-width: 430px) {
  .social-links {
    margin-top: 1.25rem;
    margin-bottom: 0.5rem;
  }
}

.social-link {
  color: rgba(60, 50, 40, 0.6);
  transition: all 0.3s ease;
}

.social-link:hover {
  color: #1a1410;
  transform: translateY(-2px);
}

/* ─── Final CTA — warm dark closer, mirrors MascotIntro ────── */
.final-cta {
  position: relative;
  width: 100%;
  padding: clamp(7rem, 10vw, 9rem) clamp(2.5rem, 6vw, 3rem);
  /* Same warm dark mocha as MascotIntro — bookends the page */
  background: linear-gradient(
    180deg,
    #17100a 0%,
    #1f1508 40%,
    #241a0c 70%,
    #17100a 100%
  );
  color: white;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  contain: paint;
}
/* Offscreen: release composited buffers for the blurred orbs + twinkle
   sparkles. visibility:hidden is cheaper than opacity:0 for the GPU. */
.final-cta-paused .cta-bg-1,
.final-cta-paused .cta-bg-2,
.final-cta-paused .cta-amb {
  visibility: hidden;
  animation-play-state: paused;
}

/* Top wave bridges from the white founder section above */
.cta-wave-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 60px;
  display: block;
  z-index: 1;
  pointer-events: none;
}

/* Cinematic blurred orbs — amber + coral.
   Desktop: filter: blur(120px) for the characteristic hazy glow.
   Mobile: no filter (blur buffers this large are what OOM iOS
   WebKit); instead a multi-stop radial-gradient produces the same
   soft-halo appearance as a painted-not-composited 2D primitive. */
.cta-bg-1,
.cta-bg-2 {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}
@media (min-width: 768px) {
  .cta-bg-1,
  .cta-bg-2 {
    filter: blur(120px);
  }
}
.cta-bg-1 {
  width: 60vw;
  height: 60vw;
  max-width: 700px;
  max-height: 700px;
  background: radial-gradient(
    circle,
    rgba(253, 173, 0, 0.22) 0%,
    transparent 70%
  );
  top: -10%;
  left: -10%;
  animation: cta-drift 24s ease-in-out infinite;
}
.cta-bg-2 {
  width: 55vw;
  height: 55vw;
  max-width: 650px;
  max-height: 650px;
  background: radial-gradient(
    circle,
    rgba(244, 132, 108, 0.18) 0%,
    transparent 70%
  );
  bottom: -15%;
  right: -10%;
  animation: cta-drift 30s ease-in-out infinite reverse;
}
@media (max-width: 767px) {
  .cta-bg-1,
  .cta-bg-2 {
    mask-image: url('~@/assets/ambient/blur-mask.svg');
    -webkit-mask-image: url('~@/assets/ambient/blur-mask.svg');
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-position: center;
  }
  .cta-bg-1 {
    background: rgba(253, 173, 0, 0.22);
  }
  .cta-bg-2 {
    background: rgba(244, 132, 108, 0.18);
  }
}
@keyframes cta-drift {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(30px, -40px);
  }
}

/* Floating ambient sparkles */
.cta-amb {
  position: absolute;
  color: rgba(253, 173, 0, 0.45);
  text-shadow: 0 0 14px rgba(253, 173, 0, 0.3);
  font-size: 1.2rem;
  z-index: 1;
  pointer-events: none;
  animation: cta-amb-twinkle 4s ease-in-out infinite;
}
.cta-amb-1 {
  top: 18%;
  left: 12%;
  animation-delay: 0s;
  font-size: 1.4rem;
}
.cta-amb-2 {
  top: 28%;
  right: 15%;
  animation-delay: 1s;
  color: rgba(244, 132, 108, 0.55);
}
.cta-amb-3 {
  bottom: 30%;
  left: 18%;
  animation-delay: 2s;
  color: rgba(253, 210, 100, 0.4);
}
.cta-amb-4 {
  bottom: 22%;
  right: 22%;
  animation-delay: 1.5s;
  font-size: 0.9rem;
  color: rgba(253, 173, 0, 0.5);
}
.cta-amb-5 {
  top: 50%;
  left: 5%;
  animation-delay: 0.5s;
  font-size: 1rem;
}

@keyframes cta-amb-twinkle {
  0%,
  100% {
    opacity: 0.2;
    transform: scale(0.7) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.3) rotate(180deg);
  }
}

/* Centered editorial container */
.cta-container {
  position: relative;
  z-index: 2;
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* "What's coming" teaser — compact glass pill list above the CTA */
.cta-coming {
  margin-bottom: clamp(3rem, 5vw, 3.75rem);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  width: 100%;
  max-width: 520px;
}
.cta-coming-label {
  font-family: 'AirbnbCereal_W_Md', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
}
.cta-coming-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;
}
.cta-coming-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem 1.15rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-family: 'AirbnbCereal_W_Bk', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.45;
  text-align: left;
  transition: background 0.3s ease, border-color 0.3s ease;
}
.cta-coming-list li > span {
  flex: 1;
  min-width: 0;
}
.cta-coming-list li:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(253, 173, 0, 0.22);
}
.cta-coming-list li strong {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 700;
  font-family: 'AirbnbCereal_W_Md', sans-serif;
}
.cta-coming-icon {
  flex-shrink: 0;
  color: #fdad00;
  opacity: 0.9;
  margin-top: 3px;
}

/* Eyebrow — amber on dark, same energy as MascotIntro eyebrow */
.cta-eyebrow {
  font-family: 'AirbnbCereal_W_Md', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: rgba(253, 173, 0, 0.9);
  margin-bottom: clamp(1.5rem, 2.5vw, 2rem);
}

/* Headline — big confident, glass-sheen gradient matching the hero "Get Out More." */
.cta-title {
  font-family: 'AirbnbCereal_W_Bd', -apple-system, sans-serif;
  font-size: clamp(2.25rem, 7vw, 3.5rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.03em;
  margin: 0 0 clamp(1rem, 2vw, 1.5rem);
  background: linear-gradient(180deg, #ffffff 0%, #f2e6ce 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 1px 0 rgba(255, 255, 255, 0.08));
}
@media (min-width: 900px) {
  .cta-title {
    font-size: clamp(3rem, 4.5vw, 4.75rem);
  }
}
.cta-title em {
  font-style: normal;
  display: inline-block;
  background: linear-gradient(
    100deg,
    #f2b24f 0%,
    #fdc86c 35%,
    #f59e2d 70%,
    #f2b24f 100%
  );
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: shine 6s ease-in-out infinite;
  filter: drop-shadow(0 10px 26px rgba(242, 178, 79, 0.22));
}

/* Subtitle */
.cta-subtitle {
  font-family: 'AirbnbCereal_W_Bk', -apple-system, sans-serif;
  font-size: clamp(1.05rem, 1.6vw, 1.3rem);
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
  margin: clamp(0.5rem, 1vw, 0.75rem) 0 clamp(2.5rem, 4vw, 2.75rem);
  max-width: 38ch;
}

/* Form wrapper */
.cta-form {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.1rem;
}

/* Z-index for click-through over orbs */
.final-cta :deep(.error-component),
.final-cta :deep(.star-button),
.final-cta :deep(a) {
  position: relative;
  z-index: 2;
  pointer-events: auto;
}

/* WaitlistRewards on dark — action buttons match the panel chrome */
.success-message :deep(.copy-btn),
.success-message :deep(.share-btn) {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.92);
  box-shadow: none;
}
.success-message :deep(.copy-btn:hover),
.success-message :deep(.share-btn:hover) {
  background: rgba(255, 255, 255, 0.18);
  box-shadow: none;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .hero-section {
    padding-top: 4rem;
    padding-bottom: 2.5rem;
    min-height: 100vh;
    height: auto;
    overflow: hidden;
  }

  .hero-content {
    gap: 1.25rem;
  }

  /* Final CTA mobile sizing handled by clamp() — no overrides needed */

  /* ─── Mobile GPU-budget hotfix ───────────────────────────────────
     iOS WebKit has a per-tab GPU memory cap that the page's full
     ambient decoration blew through. Rather than hide effects on
     mobile, we swap the expensive primitive (`filter: blur()`,
     which allocates a fresh GPU buffer per element proportional to
     radius²) for cheap equivalents (soft radial-gradients — pure
     2D painting, no compositor layer). The visual result is
     near-identical; the compositor load drops ~20×. Elements keep
     their drift/opacity animations because those run on transform,
     which is free. */
  .hero-section,
  .final-cta {
    contain: none;
  }

  .navbar-host {
    will-change: auto;
  }

  .hero-tilly-bubble,
  .messenger-bubble,
  .cta-coming-list li,
  .hero-badge {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  /* ─── content-visibility: skip offscreen sections ──────────────
     Apple's long product pages render smoothly because below-fold
     sections are "skipped" by the browser (no layout, paint, or
     composite) until they're near the viewport. contain-intrinsic-
     size gives the browser a placeholder height so the scrollbar
     still reflects total page length. Supported iOS Safari 15.4+
     and all Blink. Below-fold only — hero is always rendered. */
  .cred-strip,
  .follow-strip,
  .founder-section,
  .final-cta {
    content-visibility: auto;
    contain-intrinsic-size: auto 700px;
  }
}

/* Error message styling — white text for hero (dark bg), default dark elsewhere */
.hero-section :deep(.error-component h3) {
  color: white;
}

/* Mid panel (TillyVoiceSection with EmailCTA light variant) - black text */
:deep(.email-cta-container:not(.variant-dark)) .error-component h3 {
  color: #1a1a1a;
}

/* ─── More from Tilly ───────────────────────────────────────
   Light section, two horizontal pill tiles each with a colored
   icon square + headline + meta + diagonal arrow indicator. */
.also-section {
  width: 100%;
  padding: clamp(2.5rem, 5vw, 4rem) clamp(1.25rem, 5vw, 3rem)
    clamp(5rem, 9vw, 8rem);
  background: #ffffff;
  text-align: center;
}
.also-section > * {
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
}
.also-grid {
  width: 100%;
  max-width: 760px;
}
.also-eyebrow {
  font-family: 'AirbnbCereal_W_Md', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #8e8e93;
  margin-bottom: clamp(0.6rem, 1.2vw, 0.85rem);
}
.also-title {
  font-family: 'AirbnbCereal_W_Bd', sans-serif;
  font-size: clamp(1.85rem, 4vw, 2.4rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #1a1a1a;
  margin: 0 auto clamp(0.6rem, 1.2vw, 0.85rem);
  max-width: 18ch;
}
.also-subtitle {
  font-family: 'AirbnbCereal_W_Bk', sans-serif;
  font-size: clamp(0.95rem, 1.3vw, 1.05rem);
  line-height: 1.55;
  color: #6b7280;
  max-width: 40ch;
  margin: 0 auto clamp(1.75rem, 3.5vw, 2.5rem);
}
.also-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}
@media (min-width: 640px) {
  .also-grid {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
}
.also-tile {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.1rem;
  border-radius: 18px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-decoration: none;
  color: #1a1a1a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
    border-color 0.25s ease, box-shadow 0.25s ease;
  min-height: 64px;
  text-align: left;
}
.tile-icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.tile-visa .tile-icon {
  background: linear-gradient(135deg, #fbbf24 0%, #f97316 60%, #ea580c 100%);
  box-shadow: 0 6px 14px rgba(249, 115, 22, 0.35);
}
.tile-planner .tile-icon {
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 60%, #6d28d9 100%);
  box-shadow: 0 6px 14px rgba(124, 58, 237, 0.35);
}
.tile-text {
  flex: 1;
  min-width: 0;
}
.tile-title {
  font-family: 'AirbnbCereal_W_Bd', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
}
.tile-meta {
  font-size: 0.78rem;
  color: #6b7280;
  margin-top: 0.15rem;
  line-height: 1.3;
}
.tile-arrow {
  flex-shrink: 0;
  color: #9ca3af;
  transition: transform 0.3s ease, color 0.25s ease;
}
.also-tile:hover {
  transform: translateY(-3px);
  border-color: rgba(0, 0, 0, 0.12);
  box-shadow: 0 12px 26px rgba(0, 0, 0, 0.08);
}
.also-tile:hover .tile-arrow {
  transform: translate(2px, -2px);
  color: #1a1a1a;
}
.also-tile:hover .tile-icon {
  transform: scale(1.06) rotate(-3deg);
}
</style>
